import {
  AdminSearchMyExamRoomRequestInfosStep,
  useAdminSearchMyExamRoomRequestInfosQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { type GridEventListener, type GridRowParams, NoRowsOverlay } from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useMemo } from 'react';

import {
  BaseAdminExamLocationRequestList,
  EmptyExamLocationRequestsOverlay,
  rejectedColumns,
  type RejectedExamLocationRequest,
} from '../../components';
import { useAdminExamLocationRequestListSearchParams } from '../../hooks';

import { mapRejectedExamLocationRequestData } from './utils';

export default function RejectedAdminExamLocationRequestList() {
  const navigate = useNavigate();
  const [
    { page, pageSize, sortOrder, search, province, examCenters },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminExamLocationRequestListSearchParams();

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<RejectedExamLocationRequest>,
  ) => {
    navigate('/admin/exam-location/requests/:roomRequestId', {
      params: {
        roomRequestId: params.row.id,
      },
    });
  };

  const { data: rejectedExamLocationRequestData, loading: isGettingExamLocationRequestData } =
    useAdminSearchMyExamRoomRequestInfosQuery({
      variables: {
        adminSearchMyExamRoomRequestInfosInput: {
          filter: {
            step: AdminSearchMyExamRoomRequestInfosStep.Rejected,
            locationName: search,
            ...(examCenters.length > 0 ? { examCenterCodes: examCenters } : {}),
            ...(province.length > 0 ? { provinceId: province } : {}),
          },
          paging: {
            currentPage: page,
            pageSize: pageSize,
          },
          order: {
            field: 'verdicts.submittedAt',
            type: ORDER_TYPE[sortOrder],
          },
        },
      },
    });

  const rejectedExamLocationRequests = useMemo(() => {
    return (
      rejectedExamLocationRequestData?.ecAdminSearchMyExamRoomRequestInfos.examRoomRequestInfos.map(
        mapRejectedExamLocationRequestData,
      ) ?? []
    );
  }, [rejectedExamLocationRequestData]);

  const totalCount =
    rejectedExamLocationRequestData?.ecAdminSearchMyExamRoomRequestInfos.total ?? 0;

  const isSearchingOrFiltering = search.length > 0 || province.length > 0 || examCenters.length > 0;

  return (
    <BaseAdminExamLocationRequestList
      columns={rejectedColumns}
      rows={rejectedExamLocationRequests}
      loading={isGettingExamLocationRequestData}
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return <EmptyExamLocationRequestsOverlay title='ยังไม่มีคำขออนุมัติสถานที่ไม่อนุมัติ' />;
        },
      }}
      sortingMode='server'
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: 'verdicts.submittedAt',
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
